import React from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { DETAILS_LABEL } from '../constants';
import { claimAmount, claimName, claimStatusLabel } from '../utils';
import { CardNameTooltip } from './CardNameTooltip';
import {
    CLAIM_TAG_COLOR,
    Card,
    CardAside,
    CardBody,
    CardGrip,
    CardInfoButton,
    CardQuantity,
    CardTag,
    CardTags,
    CardTitle,
} from './blocks';

/**
 * One claim card on the split board in claims mode: the claim number and type (`CL1042 · Freight`), its status, and
 * its freight amount. A claim always moves whole, so the card has no divided state. Hovering the name shows it in
 * full, and the info button opens the claim's details.
 *
 * @param {{ claim: { claim_id: string, claim_number?: string|null, claim_type?: string|null, claim_status?: string|null, freight_amount?: number|string|null }, actions?: React.ReactNode, onShowDetails?: () => void, tooltipSuppressed?: boolean }} props
 *   `claim` is the parent's open claim from the order read. `actions` render under the details, for example the Move
 *   to menu. `onShowDetails` shows the info button and opens the details dialog. `tooltipSuppressed` keeps the name
 *   tooltip shut, for example during a drag.
 */
export const ClaimCard = ({ claim, actions = null, onShowDetails, tooltipSuppressed = false }) => {
    const title = claimName(claim);
    const status = claimStatusLabel(claim);
    const amount = claimAmount(claim);
    return (
        <Card claim aria-label={title} data-testid={`split-claim-card-${claim.claim_id}`}>
            <CardGrip aria-hidden="true">⋮⋮</CardGrip>
            <CardBody>
                <CardNameTooltip title={title} suppressed={tooltipSuppressed}>
                    <CardTitle>{title}</CardTitle>
                </CardNameTooltip>
                {status ? (
                    <CardTags>
                        <CardTag tagColor={CLAIM_TAG_COLOR}>{status}</CardTag>
                    </CardTags>
                ) : null}
                {actions}
            </CardBody>
            <CardAside>
                {amount ? <CardQuantity>{amount}</CardQuantity> : null}
                {onShowDetails ? (
                    <CardInfoButton size="small" aria-label={`${DETAILS_LABEL}: ${title}`} onClick={onShowDetails}>
                        <InfoOutlinedIcon fontSize="small" />
                    </CardInfoButton>
                ) : null}
            </CardAside>
        </Card>
    );
};

export default ClaimCard;
